import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, PlayCircle, Loader2, ShieldAlert, Mail, Download } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { saveBlobResponse } from "@/lib/downloadResponse";
import { toast } from "sonner";

const readTokenFromHash = () => {
  const h = window.location.hash || "";
  const m = h.match(/token=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : "";
};

const GuestDownload = () => {
  const { lang } = useI18n();
  const [token] = useState(readTokenFromHash);
  const [state, setState] = useState("loading"); // loading | valid | invalid
  const [items, setItems] = useState([]);
  const [expiresAt, setExpiresAt] = useState(null);
  const [busy, setBusy] = useState("");
  const [resendEmail, setResendEmail] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.post("/guest/orders/access", { token })
      .then(({ data }) => {
        if (data.valid) { setItems(data.items || []); setExpiresAt(data.expires_at); setState("valid"); }
        else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const download = async (productId, idx, label, isVideo) => {
    const key = `${productId}-${idx ?? 0}`;
    setBusy(key);
    try {
      const res = await api.post("/guest/orders/download", { token, product_id: productId, idx: idx ?? null }, { responseType: "blob" });
      // Usa filename + MIME REALI dal server (Content-Disposition/Content-Type), non la label
      saveBlobResponse(res, (label || "download"), { inline: isVideo });
    } catch {
      toast.error(lang === "en" ? "Unable to access this file." : "Impossibile accedere a questo file.");
    } finally {
      setBusy("");
    }
  };

  const resend = async () => {
    const em = resendEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { toast.error(lang === "en" ? "Enter a valid email" : "Inserisci un'email valida"); return; }
    try { await api.post("/guest/orders/resend", { email: em }); } catch {}
    toast.success(lang === "en"
      ? "If a paid order exists for this email, we've sent a new link."
      : "Se esiste un ordine pagato per questa email, ti abbiamo inviato un nuovo link.");
  };

  return (
    <div data-testid="guest-download-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
      <h1 className="text-3xl md:text-4xl font-extrabold font-heading text-brand-navy tracking-tight">
        {lang === "en" ? "Your downloads" : "I tuoi download"}
      </h1>

      {state === "loading" && (
        <div className="mt-10 flex items-center gap-3 text-brand-navy/60"><Loader2 className="h-5 w-5 animate-spin" /> {lang === "en" ? "Verifying your secure link…" : "Verifica del link sicuro in corso…"}</div>
      )}

      {state === "invalid" && (
        <div data-testid="guest-download-invalid" className="mt-8 rounded-2xl border border-brand-navy/10 bg-white p-6">
          <p className="flex items-center gap-2 font-semibold text-brand-navy"><ShieldAlert className="h-5 w-5 text-brand-orange" /> {lang === "en" ? "This link is not valid or has expired." : "Questo link non è valido o è scaduto."}</p>
          <p className="mt-2 text-sm text-brand-navy/60">{lang === "en" ? "Enter your email to receive a new secure download link." : "Inserisci la tua email per ricevere un nuovo link sicuro di download."}</p>
          <div className="mt-3 flex gap-2">
            <Input data-testid="guest-download-resend-email" type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder={lang === "en" ? "you@email.com" : "tua@email.com"} className="h-10" />
            <Button data-testid="guest-download-resend-btn" onClick={resend} className="h-10 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white whitespace-nowrap"><Mail className="h-4 w-4 mr-1.5" /> {lang === "en" ? "Send link" : "Invia link"}</Button>
          </div>
          <Link to="/" className="mt-4 inline-block text-sm text-brand-navy/50 hover:text-brand-electric">{lang === "en" ? "Back to home" : "Torna alla home"}</Link>
        </div>
      )}

      {state === "valid" && (
        <div className="mt-8 space-y-5">
          {expiresAt && (
            <p className="text-xs text-brand-navy/50">{lang === "en" ? "This secure link is valid until" : "Questo link sicuro è valido fino al"} {new Date(expiresAt).toLocaleString()}</p>
          )}
          {items.map((it) => (
            <div key={it.product_id} data-testid={`guest-item-${it.product_id}`} className="rounded-2xl border border-brand-navy/10 bg-white p-5">
              <h3 className="font-heading font-semibold text-brand-navy">{it.title}</h3>
              {it.kind === "file" && (
                <Button data-testid={`guest-dl-${it.product_id}`} disabled={!it.ready || busy === `${it.product_id}-0`} onClick={() => download(it.product_id, null, it.title, false)}
                  className="mt-3 rounded-full bg-brand-electric hover:bg-brand-electric/90 text-white">
                  {busy === `${it.product_id}-0` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> {lang === "en" ? "Download" : "Scarica"}</>}
                </Button>
              )}
              {it.kind === "guided" && (
                <ul className="mt-3 space-y-2">
                  {(it.contents || []).map((c) => (
                    <li key={c.idx} className="flex items-center justify-between gap-3 rounded-xl border border-brand-navy/10 px-3 py-2">
                      <span className="flex items-center gap-2 text-sm text-brand-navy/80 min-w-0">
                        {c.type === "video" ? <PlayCircle className="h-4 w-4 text-brand-electric shrink-0" /> : <FileText className="h-4 w-4 text-brand-electric shrink-0" />}
                        <span className="truncate">{c.label}</span>
                      </span>
                      <Button data-testid={`guest-dl-${it.product_id}-${c.idx}`} disabled={!c.ready || busy === `${it.product_id}-${c.idx}`} onClick={() => download(it.product_id, c.idx, c.label, c.type === "video")}
                        size="sm" className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white whitespace-nowrap">
                        {busy === `${it.product_id}-${c.idx}` ? <Loader2 className="h-4 w-4 animate-spin" /> : (c.type === "video" ? (lang === "en" ? "Watch" : "Guarda") : (lang === "en" ? "Download" : "Scarica"))}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuestDownload;
